import { Component, HostListener, AfterViewInit, OnInit } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, ActivatedRoute } from '@angular/router';
import { StorageService } from './services/storage/storage.service';
import { DashboardService } from './pages/dashboard/dashboard.service';
import { SearchService } from './services/search/search.service';
import { LOGIN_STATE, SharedService } from './services/shared/shared.service';
import { PredefinedTemplatesService } from './services/edit/predefined-templates.service';
import { Title, DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { ScriptsService } from './services/scripts/scripts.service';
import { RoutingService } from './services/routing/routing.service';
import { NotifyService } from './components/side-nav/notify/notify.service';
import { OpacSettingsService } from './services/opac-settings/opac-settings.service';
import { QuickMenuService } from './components/side-nav/quick-menu/quick-menu.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { YourListService } from './pages/your-list/your-list.service';
import { PatronExpiryMsgDialog } from './pages/login/dialogs/patron-expiry-msg-dialog/patron-expiry-msg-dialog.component';
import { ChangePasswordDialogComponent } from './pages/login/dialogs/changepassdialog/changepassdialog.component';
import { forkJoin } from 'rxjs';
import { NotificationsDialog } from './components/side-nav/notify/notifications-dialog/notifications-dialog.component';
import { INotifyMessage } from './components/side-nav/notify/notify-messages.interface';
import { AccountService } from './pages/account/account.service';
import { IllSettingsService } from './services/ill-settings/ill-settings.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { LayoutService } from './services/layout.service';
import { DIALOG_STYLE } from '../assets/css/ag/styles';
import { TranslateService } from '@ngx-translate/core';

export interface LoginData {
  cid: string;
  lid: string;
  username: string;
  password: string;
  sessionId: string;
}

// ?
// ignore the following error throws by ngx-medium-editor for angular 10
// https://stackoverflow.com/questions/62755093/angular-error-generic-type-modulewithproviderst-requires-1-type-arguments
//node_modules/ngx-medium-editor/index.d.ts:4:23 - error TS2314: Generic type 'ModuleWithProviders<T>' requires 1 type argument(s). 4 static forRoot(): ModuleWithProviders;
/* eslint-disable-next-line */
declare module '@angular/core' {
  interface ModuleWithProviders<T = any> {
    ngModule: Type<T>;
    providers?: Array<Provider | EnvironmentProviders>;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
/**
 * Main purpose of the AppComponent is to bootstrap the application
 * with the data it needs, with minimal amount of code or html.
 */
export class AppComponent implements AfterViewInit, OnInit {
  private _cid: any;
  private _lid: any;

  authorized = false;

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    this._quickMenuService.hotkey(event);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this._sharedService.setResizeBS(window.innerWidth);
  }

  constructor(
    private _router: Router,
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _accountService: AccountService,
    private _dashboardService: DashboardService,
    private _notifyService: NotifyService,
    private _opacSettingsService: OpacSettingsService,
    private _predefinedTemplatesService: PredefinedTemplatesService,
    private _routingService: RoutingService,
    private _searchService: SearchService,
    private _scriptsService: ScriptsService,
    private _sharedService: SharedService,
    private _storageService: StorageService,
    private _titleService: Title,
    private _quickMenuService: QuickMenuService,
    private _yourListService: YourListService,
    private _illSettingService: IllSettingsService,
    private _layoutService: LayoutService,
    private _activatedRoute: ActivatedRoute,
    private _translateService: TranslateService,

    // mat icon loaders
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
  ) {
    iconRegistry.addSvgIcon(
      'finger-black',
      sanitizer.bypassSecurityTrustResourceUrl('assets/icons/finger-gray.svg'),
    );

    iconRegistry.addSvgIcon(
      'finger',
      sanitizer.bypassSecurityTrustResourceUrl('assets/icons/finger-black.svg'),
    );

    iconRegistry.addSvgIcon(
      'finger-inverse',
      sanitizer.bypassSecurityTrustResourceUrl('assets/icons/finger-inverse.svg'),
    );

    iconRegistry.addSvgIcon(
      'browse-search-icon',
      sanitizer.bypassSecurityTrustResourceUrl('assets/icons/browse-search-icon.svg'),
    );
    this._translateService.addLangs(['en', 'es']);
    this._translateService.setDefaultLang('en');
    this._translateService.use('en');
  }

  ngOnInit() {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this._snackbar.dismiss();
      }
      if (event instanceof NavigationEnd) {
        this._setCurrentPageTitle();
        this._routingService.setCurrentUrl(event.url);

        if (this._router.url.includes('/kids') || this._activatedRoute.snapshot.queryParams?.kids) {
          this._sharedService.setKidsCat(true);
        }
      }
    });

    this._initializeSubscriptions();

    this._sharedService.setDefaultWindowSize(window.innerWidth);
  }

  ngAfterViewInit() {
    this._loadDataTablesScriptStylesLazy();
  }

  private _initializeSubscriptions(): void {
    this._sharedService.userData$.subscribe((userData) => {
      if (!userData) {
        return;
      }

      this.authorized = true;
      this._sharedService.setLoginStatusBS(LOGIN_STATE.SUCCESSFUL);

      if (userData.isPatron) {
        this._checkIfPatronExpiryDialog(userData);
      }

      if (userData.needsPasswordChange) {
        this._openChangePasswordDialog(
          userData.libraryOptions.patronPasswordNumeric && userData.isPatron,
        );
      }

      if (userData.guestListSaved) {
        this._sharedService.openSnackBar('Guest List Added.');
      }

      this._searchService.resultType = userData.opacOptions.searchResultOptions.groupOption;
      this._searchService.sortBy = userData.opacOptions.searchResultOptions.sortBy;
      this._searchService.orderBy = userData.opacOptions.searchResultOptions.orderBy;

      const observableToFork = [];
      observableToFork.push(this._searchService.getSearchIndexes());
      observableToFork.push(this._searchService.getAvailableTargets());
      observableToFork.push(this._opacSettingsService.loadWebLinkOptions());
      observableToFork.push(this._opacSettingsService.fetchOpacResults());
      observableToFork.push(this._accountService.getUserPreferences());
      observableToFork.push(this._illSettingService.getBorrowerPolicy());

      //TODO Ensure BE return empty list for guests
      observableToFork.push(this._yourListService.loadLists());

      if (userData.isStaff) {
        observableToFork.push(this._dashboardService.loadStaffDashboard());
        observableToFork.push(this._quickMenuService.loadQuickMenu());
      }

      forkJoin(observableToFork).subscribe(() => {
        this._sharedService.setLoginStatusBS(LOGIN_STATE.COMPLETE);
      });

      this._predefinedTemplatesService.getLibAndBindInfoAsync(
        this._storageService.getCid(),
        this._storageService.getLid(),
      ); // get latest library pages

      if (userData.isStaff || userData.isPatron) {
        this._notifyService.startUnreadMessageCountUpdates();
        this._notifyService.getNotificationMessages().subscribe((messages) => {
          this._showAlertNotifications(messages);
        });
      }
    });

    this._sharedService.getLibraryChangedBS().subscribe((values) => {
      if (!values) {
        return;
      }

      this._cid = values.cid.toUpperCase();
      this._lid = values.lid.toUpperCase();
      this._setCurrentPageTitle();
      this._getLayoutSettings(this._cid, this._lid);
    });
  }

  async _getLayoutSettings(cid: string, lid: string) {
    const layoutSettings = await this._predefinedTemplatesService.getLayoutSettingsAsync(cid, lid);

    this._layoutService.setLayoutSettings(layoutSettings);
  }

  /**
   * Loads Data Tables
   */
  private _loadDataTablesScriptStylesLazy(): void {
    this._scriptsService.load('dataTables').catch((error) => console.log(error));
  }

  private _setCurrentPageTitle() {
    let title = this._routingService.getCurrentRouteTitle();

    if (this._cid) {
      title += ` | ${this._cid}`;
    }

    if (this._lid) {
      title += ` | ${this._lid}`;
    }

    title += ' | Auto-Graphics Inc.';
    this._titleService.setTitle(title);
  }

  private _checkIfPatronExpiryDialog(userData): void {
    if (!userData.circOption && !userData.expireDate) {
      return;
    }

    const patronExpirationWarningPeriod =
      userData.circOption.patronExpirationWarningPeriod * 24 * 60 * 60 * 1000;
    const timeUntilUserExpiration: number =
      new Date(userData.expireDate).getTime() - new Date().getTime();

    if (timeUntilUserExpiration <= patronExpirationWarningPeriod) {
      this._dialog.open(PatronExpiryMsgDialog, {
        data: userData,
        panelClass: DIALOG_STYLE.MD,
      });
    }
  }

  private _openChangePasswordDialog(isPasswordNumeric): void {
    const dialogRef = this._dialog.open(ChangePasswordDialogComponent, {
      disableClose: true,
      panelClass: DIALOG_STYLE.MD,
      data: {
        isPasswordNumeric: isPasswordNumeric,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._sharedService.openSnackBar('Password updated successfully');
        this._router.navigate(['/'], {
          queryParams: {
            cid: this._storageService.getCid(),
            lid: this._storageService.getLid(),
          },
        });
      }
    });
  }

  private _showAlertNotifications(notifications: INotifyMessage): void {
    const allMessages = notifications?.allMessages;
    if (allMessages) {
      allMessages.forEach((msg) => {
        if (!msg.msgBegin.includes('Z')) {
          msg.msgBegin = msg.msgBegin.concat('Z');
        }

        // preserving line breaks that come from DB
        msg.msgText = msg.msgText.replace(/(?:\r\n|\r|\n)/g, '<br/>');
      });

      for (let index = 0; index < allMessages.length; index++) {
        const msg = allMessages[index];
        if (msg.showAlertBox) {
          this._openNotificationsDialog(msg, index);
        }
      }
    }
  }

  private _openNotificationsDialog(element, position): void {
    const dialogRef = this._dialog.open(NotificationsDialog, {
      panelClass: 'notifications-back-color',
      width: '600px',
      height: 'auto',
      disableClose: true,
      data: {
        position: position,
        message: element,
        fromHeader: true,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.message) {
        this._notifyService.ackMessage(result.message).subscribe((response) => {
          if (response.status === '200 OK') {
            this._notifyService.updateUnreadMessageCount();
          }
        });
      }
    });
  }
}
